import { DateTime } from 'luxon';
import * as React from 'react';
import { Box } from '../../designSystem/components/box';
import { Flex } from '../../designSystem/components/flex';
import {
    DocumentUserPlanningFragment,
    EventsDocumentInput,
    UserPlanningDocumentOptions
} from '../../generated/types';
import { DateTimeService } from '../../services/dateTimeService';
import { isValidColor } from '../../util/color';
import { useService, useTranslate } from '../../util/dependencies/dependencies';
import { LocaleFormats } from '../../util/luxon';
import { A4_SIZES } from '../../util/pdf';
import { isNonEmptyString } from '../../util/string';
import { DocumentInformations } from '../common/documentInformations';
import { DocumentLeftContainer } from '../common/documentLeftContainer';
import { DocumentQrCode } from '../common/documentQrCode';

interface IUserPlanningContainerProps {
    children?: React.ReactNode;
    document: EventsDocumentInput;
    options: UserPlanningDocumentOptions;
    event: DocumentUserPlanningFragment;
    startDate?: DateTime;

    renderText(text: string): string;
}

export const UserPlanningContainer = (props: IUserPlanningContainerProps) => {
    const translate = useTranslate();
    const dateTimeService = useService(DateTimeService);
    const vr = props.event.volunteerRegistration;
    const ui = vr.userInfo;
    const start = props.startDate
        ? DateTime.max(props.startDate, props.event.startAt)
        : props.event.startAt;
    const end = props.startDate
        ? DateTime.min(props.startDate.plus({ week: 1 }).minus({ day: 1 }), props.event.endAt)
        : props.event.endAt;
    const headerColor = isValidColor(props.options.headerColor)
        ? props.options.headerColor
        : 'white';
    const showLeft = props.options.showInformations || props.options.showQrCode;

    return (
        <Flex
            css={{
                color: isValidColor(props.options.textColor) ? props.options.textColor : '$gray800',
                background: isNonEmptyString(props.options.background?.content)
                    ? `url(${props.options.background?.content})`
                    : '#5230a1',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                fontFamily: props.options.fontFamily || 'Open Sans',
                fontSize: '12px',
                pageBreakBefore: 'always',
                position: 'relative'
            }}
            height={A4_SIZES['96dpi'].width}
            width={A4_SIZES['96dpi'].height}
        >
            <Flex
                align="end"
                css={{
                    position: 'absolute',
                    right: '16px',
                    top: '69px'
                }}
                direction="column"
                gap="1"
            >
                <Box
                    css={{
                        color: headerColor,
                        fontSize: '13px',
                        fontWeight: '700',
                        lineHeight: '18px'
                    }}
                >
                    {translate('planning_indivi_05075', ui.nameOrEmail)}
                </Box>

                <Box
                    css={{
                        color: headerColor,
                        fontSize: '24px',
                        fontWeight: '700',
                        textTransform: 'uppercase'
                    }}
                >
                    {translate(
                        'du_1_au_2_12974',
                        dateTimeService.toLocaleString(start, LocaleFormats.DateOnly.MonthLong),
                        dateTimeService.toLocaleString(end, LocaleFormats.DateOnly.MonthLong)
                    )}
                </Box>
            </Flex>

            <Flex
                css={{
                    px: '$4',
                    position: 'absolute',
                    top: '140px'
                }}
                gap="5"
                height={635}
                width={1}
            >
                {showLeft && (
                    <DocumentLeftContainer>
                        <DocumentInformations
                            informationsContent={props.document.informationsContent}
                            informationsTitle={props.document.informationsTitle}
                            options={props.options}
                            renderText={props.renderText}
                        />

                        <DocumentQrCode options={props.options} ticketId={vr.ticketId} />
                    </DocumentLeftContainer>
                )}

                <Flex
                    css={{
                        background: 'white',
                        borderRadius: '$3',
                        flex: '1',
                        fontSize: '12px',
                        padding: '$6 $5'
                    }}
                    height={1}
                >
                    <Box width={1}>{props.children}</Box>
                </Flex>
            </Flex>
        </Flex>
    );
};
